import React from 'react';
import { ExpandMore } from '@material-ui/icons';
import {
    TextField,
    withStyles,
    FormControl,
    InputLabel,
    FormGroup,
    Checkbox,
    FormControlLabel,
    RadioGroup,
    Radio,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@material-ui/core';
import { PRODUCT_TYPES, TRIGGER_DEFINITIONS, PRODUCT_TYPE_TRIGGERS, PRODUCT_TYPE_BEHAVIOURS, capabilitiesForType } from './constants';

const styles = theme => ({
    accordionHeading: {
        flexBasis: '33%',
        flexShrink: 0
    },
    accordionSubHeading: {
        color: theme.palette.text.secondary
    }
});

// Renders one collapsible panel per product type, holding the triggers and behaviours
// that both the product type and the external system type support. Values live under
// values.ProductTypeSettings[<ProductType>] so plain Formik handleChange works via the
// dotted field name.
const ProductTypePanels = props => {
    const {
        classes,
        externalType,
        translations,
        values,
        errors,
        touched,
        handleChange,
        handleBlur
    } = props;

    const capabilities = capabilitiesForType(externalType);

    return (
        <>
            <InputLabel shrink>{translations['product_type_settings'] || "Product type triggers & behaviours"}</InputLabel>
            {PRODUCT_TYPES.map(productType => {
                const type = productType.key;
                const typeValues = values.ProductTypeSettings?.[type];
                if (!typeValues) {
                    return null;
                }
                const typeErrors = errors?.ProductTypeSettings?.[type] ?? {};
                const typeTouched = touched?.ProductTypeSettings?.[type] ?? {};
                const availableTriggers = capabilities.triggers
                    ? TRIGGER_DEFINITIONS.filter(trigger => PRODUCT_TYPE_TRIGGERS[type].includes(trigger.name))
                    : [];
                const enabledTriggerCount = availableTriggers.filter(trigger => typeValues[trigger.name]).length;
                const showProvideExternalID = capabilities.provideExternalID && values.Direction.includes('in');
                const showConfirmDataExport = capabilities.confirmDataExport
                    && values.Direction.includes('out')
                    && PRODUCT_TYPE_BEHAVIOURS[type].includes('ConfirmDataExport');
                const showDiagnoseCases = capabilities.diagnoseCases
                    && PRODUCT_TYPE_BEHAVIOURS[type].includes('SendPerformedDiagnoseCasesOnly');
                const showBehaviours = showDiagnoseCases || showProvideExternalID || showConfirmDataExport;

                return (
                    <Accordion key={type}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <span className={classes.accordionHeading}>{translations[productType.translationKey] || productType.fallback}</span>
                            {capabilities.triggers ?
                                <span className={classes.accordionSubHeading}>
                                    {`${enabledTriggerCount}/${availableTriggers.length} ${translations['triggers'] || 'triggers'}`}
                                </span> : null
                            }
                        </AccordionSummary>
                        <AccordionDetails style={{ display: 'block' }}>
                            {capabilities.triggers ?
                                <>
                                    <InputLabel shrink>{translations['select_external_triggers'] || "Please select triggers for external service"}</InputLabel>
                                    <FormGroup row>
                                        {availableTriggers.map(trigger => (
                                            <FormControlLabel
                                                key={trigger.name}
                                                control={
                                                    <Checkbox
                                                        checked={typeValues[trigger.name]}
                                                        onChange={handleChange}
                                                        name={`ProductTypeSettings.${type}.${trigger.name}`}
                                                        color="primary"
                                                    />
                                                }
                                                label={translations[trigger.translationKey] || trigger.fallback}
                                            />
                                        ))}
                                    </FormGroup>
                                </> : null
                            }
                            {capabilities.report ?
                                <>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={typeValues.TriggerOnReportGenerated}
                                                onChange={handleChange}
                                                name={`ProductTypeSettings.${type}.TriggerOnReportGenerated`}
                                                color="primary"
                                            />
                                        }
                                        label={translations['external_send_erasure_report'] || 'Send erasure report when generated?'}
                                    />
                                    {typeValues.TriggerOnReportGenerated ?
                                        <>
                                            {capabilities.report === 'full' ?
                                                <FormGroup row>
                                                    <FormControl>
                                                        <RadioGroup
                                                            row
                                                            name={`ProductTypeSettings.${type}.SendReportMethod`}
                                                            value={typeValues.SendReportMethod}
                                                            onChange={handleChange}
                                                        >
                                                            <FormControlLabel
                                                                value="base64pdf"
                                                                control={<Radio />}
                                                                label={`${translations['send_pdf_report_base64_encoded'] || 'Send pdf report base64 encoded to a separate endpoint'}`}
                                                            />
                                                            <FormControlLabel
                                                                value="downloadLink"
                                                                control={<Radio />}
                                                                label={`${translations['send_report_download_link'] || 'Send download link for report along with other data (table mapping)'}`}
                                                            />
                                                        </RadioGroup>
                                                    </FormControl>
                                                </FormGroup> : null
                                            }
                                            <FormGroup row>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={typeValues.SendReportOnSuccessfulErasure}
                                                            onChange={handleChange}
                                                            name={`ProductTypeSettings.${type}.SendReportOnSuccessfulErasure`}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={translations['send_report_on_successful_erasure'] || 'Send report on successful erasure'}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={typeValues.SendReportOnFailedErasure}
                                                            onChange={handleChange}
                                                            name={`ProductTypeSettings.${type}.SendReportOnFailedErasure`}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={translations['send_report_on_failed_erasure'] || 'Send report on failed erasure'}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={typeValues.SendReportOnNoErasure}
                                                            onChange={handleChange}
                                                            name={`ProductTypeSettings.${type}.SendReportOnNoErasure`}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={translations['send_report_on_no_erasure'] || 'Send report on no erasure'}
                                                />
                                            </FormGroup>
                                            {capabilities.report === 'full' && typeValues.SendReportMethod === 'base64pdf' ?
                                                <TextField
                                                    name={`ProductTypeSettings.${type}.SendErasureReportEndpoint`}
                                                    value={typeValues.SendErasureReportEndpoint}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={!!(typeErrors.SendErasureReportEndpoint && typeTouched.SendErasureReportEndpoint)}
                                                    placeholder={translations["external_send_report_endpoint"] || "Endpoint for sending erasure report."}
                                                    label={translations["external_send_report_endpoint"] || "Endpoint for sending erasure report."}
                                                    margin={"dense"}
                                                    fullWidth={true}
                                                /> : null
                                            }
                                        </>
                                        : null
                                    }
                                </> : null
                            }
                            {showBehaviours ?
                                <>
                                    <br />
                                    <InputLabel shrink>{translations['select_external_behaviour'] || "Please select client behaviours"}</InputLabel>
                                </> : null
                            }
                            {showDiagnoseCases ?
                                <FormGroup row>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={typeValues.SendPerformedDiagnoseCasesOnly}
                                                onChange={handleChange}
                                                name={`ProductTypeSettings.${type}.SendPerformedDiagnoseCasesOnly`}
                                                color="primary"
                                            />
                                        }
                                        label={translations['send_performed_diagnose_cases_only'] || 'Send performed diagnose cases only (DiagnoseResults must be included in table mapping)'}
                                    />
                                </FormGroup> : null
                            }
                            {showProvideExternalID ?
                                <FormGroup row>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={typeValues.ProvideExternalIDEnabled}
                                                onChange={handleChange}
                                                name={`ProductTypeSettings.${type}.ProvideExternalIDEnabled`}
                                                color="primary"
                                            />
                                        }
                                        label={translations['external_behaviour_provide_external_id'] || 'Provide External ID on device check'}
                                    />
                                    {typeValues.ProvideExternalIDEnabled ?
                                        <>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={typeValues.ProvideExternalIDOptional}
                                                        onChange={handleChange}
                                                        name={`ProductTypeSettings.${type}.ProvideExternalIDOptional`}
                                                        color="primary"
                                                    />
                                                }
                                                label={translations['external_id_optional'] || 'ExternalID optional'}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={typeValues.ProvideExternalIDAllowDuplicate}
                                                        onChange={handleChange}
                                                        name={`ProductTypeSettings.${type}.ProvideExternalIDAllowDuplicate`}
                                                        color="primary"
                                                    />
                                                }
                                                label={translations['external_id_allow_duplicate'] || 'Allow duplicate External IDs (multiple devices may share one External ID)'}
                                            />
                                            <TextField
                                                name={`ProductTypeSettings.${type}.ProvideExternalIDRegexp`}
                                                value={typeValues.ProvideExternalIDRegexp}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={!!(typeErrors.ProvideExternalIDRegexp && typeTouched.ProvideExternalIDRegexp)}
                                                placeholder={translations["external_id_regexp"] || "External ID Regexp (Leave empty for no regexp check)"}
                                                label={translations["external_id_regexp"] || "External ID Regexp (Leave empty for no regexp check)"}
                                                margin={"dense"}
                                                fullWidth={true}
                                            />
                                            <TextField
                                                name={`ProductTypeSettings.${type}.ProvideExternalIDDescription`}
                                                value={typeValues.ProvideExternalIDDescription}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={!!(typeErrors.ProvideExternalIDDescription && typeTouched.ProvideExternalIDDescription)}
                                                placeholder={translations["external_id_regexp_description"] || "External ID Regexp Description"}
                                                label={translations["external_id_regexp_description"] || "External ID Regexp Description"}
                                                margin={"dense"}
                                                fullWidth={true}
                                            />
                                            <TextField
                                                name={`ProductTypeSettings.${type}.ProvideExternalIDName`}
                                                value={typeValues.ProvideExternalIDName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={!!(typeErrors.ProvideExternalIDName && typeTouched.ProvideExternalIDName)}
                                                placeholder={translations["external_id_name"] || "External ID Name on External Service"}
                                                label={translations["external_id_name"] || "External ID Name on External Service"}
                                                margin={"dense"}
                                                fullWidth={true}
                                            />
                                        </> : null
                                    }
                                </FormGroup> : null
                            }
                            {showConfirmDataExport ?
                                <FormGroup row>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={typeValues.ConfirmDataExportEnabled}
                                                onChange={handleChange}
                                                name={`ProductTypeSettings.${type}.ConfirmDataExportEnabled`}
                                                color="primary"
                                            />
                                        }
                                        label={translations['external_behaviour_confirm_data_export'] || 'Confirm data export'}
                                    />
                                    {typeValues.ConfirmDataExportEnabled ?
                                        <TextField
                                            name={`ProductTypeSettings.${type}.ConfirmDataExportMessage`}
                                            value={typeValues.ConfirmDataExportMessage}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(typeErrors.ConfirmDataExportMessage && typeTouched.ConfirmDataExportMessage)}
                                            placeholder={translations["confirm_data_export_message"] || "Message for data export finished"}
                                            label={translations["confirm_data_export_message"] || "Message for data export finished"}
                                            margin={"dense"}
                                            fullWidth={true}
                                        /> : null
                                    }
                                </FormGroup> : null
                            }
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </>
    );
};

export default withStyles(styles, { withTheme: true })(ProductTypePanels);
